import { FlowTypes } from 'v2/types'
import { useHasVoiceNode } from '../../hooks/use-has-voice-node'
import { useFlowRunStore, useFlowStore } from '../../store'
import { RunType } from '../store'

interface RunLogsUIInput {
  runType?: RunType
  hasVoiceNode: boolean
  isChat: boolean
}

export interface RunLogsUI {
  showNewUI: boolean
  isChat: boolean
}

/**
 * Which run-detail layout to render.
 *
 * The rich layout (per-node conversation cards, tool and knowledge-base cards,
 * execution details) was built for voice and gated on the flow having a voice
 * node. Chat and SMS runs carry the same transcript, tool and knowledge-base
 * payloads, so they render the same way. Other channels keep the legacy
 * two-tab card until their payloads are verified.
 */
export const resolveRunLogsUI = ({ runType, hasVoiceNode, isChat }: RunLogsUIInput): RunLogsUI => ({
  isChat,
  showNewUI: runType === RunType.FlowRunLogs && (hasVoiceNode || isChat),
})

/**
 * The flow types whose runs are text-agent transcripts. `isChat` downstream
 * means exactly this (transcript tabs, Start-node opening chunk, toggle) —
 * SMS joins it deliberately; WhatsApp and voice do not. An SMS flow that
 * contains a voice node keeps today's voice view (latency, call stats) —
 * only chat overrides voice, exactly as before.
 */
export const isTextAgentRun = (
  flowType: FlowTypes | string | null | undefined,
  hasVoiceNode: boolean,
): boolean =>
  flowType === FlowTypes.incoming_chat || (flowType === FlowTypes.incoming_sms && !hasVoiceNode)

export const useFlowRunLogsUI = (): RunLogsUI => {
  const hasVoiceNode = useHasVoiceNode()
  const currentFlowType = useFlowStore((state) => state.currentFlowType)
  const runType = useFlowRunStore((state) => state.runType)

  return resolveRunLogsUI({
    runType,
    hasVoiceNode,
    isChat: isTextAgentRun(currentFlowType, hasVoiceNode),
  })
}
